// Input handling functions - keyboard, mouse, and touch

import type p5 from 'p5';
import {
    getGameState, setGameState,
    getCanRestart,
    getGalleryCategory, setGalleryCategory,
    getGalleryIndex, setGalleryIndex,
    setGalleryStartTime, setGalleryLoadingTimer,
    getTitleTimer,
    setGalleryShipPage,
    getPlayerStateMachine,
    clearHighScore
} from '../../store/index';
import { handleShipSelect } from '../../ui/screens/ship-select';
import { isRetryButtonClicked } from '../../ui/screens/game-over';
import { isGameStartButtonClicked, isGalleryButtonClicked, isClearHighScoreButtonClicked } from '../../ui/screens/start-screen';
import { isLeftArrowClicked, isRightArrowClicked, isBackButtonClicked, isCategoryTabClicked, isShipPageTabClicked } from '../../ui/screens/gallery';

import {
    getKeyboardTriggeredActions,
    getMouseTriggeredActions
} from './input-utils';

/**
 * keyPressed() - キーボード入力処理
 * 矢印キーやWASDキーで移動、スペースで発射、Sで特殊能力
 * @param p - P5.jsインスタンス
 */
export function keyPressed(p: p5): boolean {
    const gameState = getGameState();

    if (gameState === 'playing') {
        const actions = getKeyboardTriggeredActions(p);
        const machine = getPlayerStateMachine();
        for (const action of actions) {
            machine.handleAction(action);
        }
    }

    return false; // ブラウザのデフォルト動作を防ぐ
}

/**
 * mousePressed() - マウスクリック処理
 * タップ/クリック操作に対応
 * @param p - P5.jsインスタンス
 */
export function mousePressed(p: p5): void {
    const gameState = getGameState();
    const canRestart = getCanRestart();

    if (gameState === 'start') {
        // タイトル画面のボタン判定
        if (isClearHighScoreButtonClicked()) {
            // ハイスコアクリアボタン
            clearHighScore();
        } else if (isGameStartButtonClicked()) {
            setGameState('select');
        } else if (isGalleryButtonClicked()) {
            setGameState('gallery');
            setGalleryCategory('ships');
            setGalleryIndex(0);
            setGalleryStartTime(getTitleTimer());  // 開始時刻を記録
        }
    } else if (gameState === 'gallery') {
        // ギャラリー画面の操作処理
        const category = getGalleryCategory();
        const index = getGalleryIndex();

        // BACKボタン
        if (isBackButtonClicked()) {
            setGameState('start');
            return;
        }

        // カテゴリタブクリック
        const clickedTab = isCategoryTabClicked();
        if (clickedTab && (clickedTab === 'ships' || clickedTab === 'enemies' || clickedTab === 'stages')) {
            setGalleryCategory(clickedTab);
            setGalleryIndex(0);
            setGalleryLoadingTimer(30);  // 30フレーム（約0.5秒）のローディング演出
            setGalleryShipPage(0);  // ページをリセット
            return;
        }

        // 機体ギャラリーのページタブクリック
        if (category === 'ships') {
            const clickedPage = isShipPageTabClicked();
            if (clickedPage !== null) {
                setGalleryShipPage(clickedPage);
                return;
            }
        }

        // 左矢印
        if (isLeftArrowClicked() && index > 0) {
            setGalleryIndex(index - 1);
            setGalleryShipPage(0);  // ページをリセット
            return;
        }

        // 右矢印
        const maxIndex = category === 'ships' ? 2 : category === 'enemies' ? 7 : 4;
        if (isRightArrowClicked() && index < maxIndex) {
            setGalleryIndex(index + 1);
            setGalleryShipPage(0);  // ページをリセット
            return;
        }
    } else if (gameState === 'select') {
        // 機体選択画面
        handleShipSelect();
    } else if (gameState === 'gameover') {
        // リトライボタンをクリックした場合のみリスタート
        if (canRestart && isRetryButtonClicked()) {
            setGameState('select');  // 機体選択に戻る
        }
    } else if (gameState === 'playing') {
        const actions = getMouseTriggeredActions(p);
        const machine = getPlayerStateMachine();
        for (const action of actions) {
            machine.handleAction(action);
        }
    }
}

/**
 * touchStarted() - タッチ入力処理
 * スマートフォン対応
 * @param p - P5.jsインスタンス
 */
export function touchStarted(p: p5): boolean {
    mousePressed(p); // マウスクリックと同じ処理を実行
    return false;   // ブラウザのデフォルト動作を防止
}
